//@flow
import React, { Component } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import { FormattedMessage } from 'react-intl';
import getter from 'dibs-object-getter';
import Dropzone from 'react-dropzone';

import { ProfileHeaderBreadcrumbs } from './Header/ProfileHeaderBreadcrumbs';
import { ProfileHeaderData } from './Header/ProfileHeaderData';
import ProgressTour from './Header/ProgressTour';

import { logError } from '../helpers';
import { profileMediaMutation } from './mutations/profileMedia';
import { upload, SPLASH_IMAGE_TYPE } from './helpers/imageUploader';

import { Spinner } from 'dibs-elements/exports/Spinner';
import ImageUpload from 'dibs-icons/exports/ImageUpload';

import type { HeaderRoot_viewer as Viewer } from './__generated__/HeaderRoot_viewer.graphql';
import type { PageType } from './constants';
import type { DropzoneFile } from 'react-dropzone';
import type { RelayProp } from 'react-relay';

import styles from './HeaderRoot.scss';

type Props = {
    viewer: ?Viewer,
    currentPage: PageType,
    onChangePage: (pageType: PageType) => void,
    isEditMode: boolean,
    relay?: RelayProp,
};

type State = {
    isSaving: boolean,
    splashPhotoPath: ?string,
    showError: boolean,
};

export class HeaderRootComponent extends Component<Props, State> {
    static getSplashPhotoPath(props: Props): ?string {
        return getter(props.viewer)
            .at('tradeFirms')
            .at(0)
            .at('publicProfile')
            .value('splashPhoto');
    }

    constructor(props: Props) {
        super(props);
        this.state = {
            isSaving: false,
            splashPhotoPath: HeaderRootComponent.getSplashPhotoPath(props),
            showError: false,
        };
    }

    onDrop = (
        acceptedFiles: Array<DropzoneFile> = [],
        rejectedFiles: Array<DropzoneFile> = [], // eslint-disable-line no-unused-vars
        event: SyntheticDragEvent<>, // eslint-disable-line no-unused-vars
        callback?: () => void
    ) => {
        return this.uploadImage(acceptedFiles[0], callback);
    };

    uploadImage = (file?: DropzoneFile, callback?: () => void) => {
        this.setState({ isSaving: true, showError: false }, () => {
            const firmId = getter(this.props.viewer)
                .at('tradeFirms')
                .at(0)
                .value('serviceId');
            if (!file || !firmId) {
                this.setState({ isSaving: false });
                return null;
            }
            return upload(file, firmId, SPLASH_IMAGE_TYPE)
                .then(result => {
                    if (result && result.hasError) {
                        this.setState({
                            isSaving: false,
                            splashPhotoPath: HeaderRootComponent.getSplashPhotoPath(this.props),
                            showError: true,
                        });
                        return { loggedErrorAlready: true };
                    }

                    // update the preview after save, since it'll be disposed
                    const splashPhotoPath = getter(result)
                        .at('image')
                        .value('path');

                    this.setState({ splashPhotoPath });

                    const splashPhotoId =
                        getter(result)
                            .at('image')
                            .value('id') || /* istanbul ignore next */ '';

                    const profilePhotoId =
                        getter(this.props.viewer)
                            .at('tradeFirms')
                            .at(0)
                            .at('publicProfile')
                            .value('profilePhotoId') || /* istanbul ignore next */ '';

                    if (!this.props.relay) {
                        throw new Error('No relay prop found for mutation');
                    }

                    return profileMediaMutation(this.props.relay.environment, {
                        serviceId: firmId + '',
                        profilePhotoId: profilePhotoId + '',
                        splashPhotoId: splashPhotoId + '',
                    });
                })
                .then(profileResult => {
                    if (profileResult && profileResult.loggedErrorAlready) {
                        // istanbul ignore next
                        if (callback && typeof callback === 'function') {
                            callback();
                        }
                        return;
                    } else if (profileResult && profileResult.hasError) {
                        this.setState({
                            isSaving: false,
                            splashPhotoPath: HeaderRootComponent.getSplashPhotoPath(this.props),
                        });
                        // istanbul ignore next
                        if (callback && typeof callback === 'function') {
                            callback();
                        }
                        return;
                    }

                    this.setState({ isSaving: false, showError: false });
                    // istanbul ignore next
                    if (callback && typeof callback === 'function') {
                        callback();
                    }
                })
                .catch(e => {
                    logError(e);
                    this.setState(
                        {
                            isSaving: false,
                            showError: true,
                            splashPhotoPath: HeaderRootComponent.getSplashPhotoPath(this.props),
                        },
                        () => {
                            // istanbul ignore next
                            if (callback && typeof callback === 'function') {
                                callback();
                            }
                        }
                    );
                });
        });
    };

    render() {
        const { isEditMode, currentPage } = this.props;
        const [tradeFirm] =
            getter(this.props)
                .at('viewer')
                .value('tradeFirms') || [];

        if (!tradeFirm) {
            return null;
        }

        let splashPhoto = this.state.isSaving
            ? this.state.splashPhotoPath
            : getter(tradeFirm)
                  .at('publicProfile')
                  .value('splashPhoto');

        // istanbul ignore else
        if (splashPhoto && splashPhoto.length) {
            splashPhoto = `${splashPhoto}?width=1472`; // iphone plus model in landscape (736px) @ 2x
        }

        const headerStyles = {
            background: `url("${splashPhoto || /* istanbul ignore next */ ''}")`,
            backgroundSize: 'cover',
        };

        const geography = getter(this.props)
            .at('viewer')
            .value('geography');

        let uploadIcon = null;

        if (isEditMode) {
            uploadIcon = (
                <Dropzone accept="image/jpeg" multiple={false} onDrop={this.onDrop}>
                    {dropzoneProps => {
                        return (
                            <div
                                {...dropzoneProps.getRootProps()}
                                className={styles.uploadIconWrapper}
                            >
                                <input
                                    {...dropzoneProps.getInputProps()}
                                    data-tn="upload-bg-image-input"
                                />
                                {splashPhoto ? (
                                    <FormattedMessage
                                        id="abt.designProfile.header.splashPhotoUpdatePrompt"
                                        defaultMessage="Change your background photo"
                                    />
                                ) : (
                                    <FormattedMessage
                                        id="abt.designProfile.header.splashPhotoUploadPrompt"
                                        defaultMessage="Add a background photo"
                                    />
                                )}
                                <ImageUpload className={styles.imageUploadSvg} />
                            </div>
                        );
                    }}
                </Dropzone>
            );
        }

        return (
            <div className={styles.wrapper}>
                <ProgressTour isEditMode={isEditMode} tradeFirm={tradeFirm || null} />
                <div className={styles.firmInfoContainer} style={headerStyles}>
                    {this.state.showError && (
                        <div className={styles.splashPhotoError}>
                            <FormattedMessage
                                id="abt.designProfile.header.splashPhotoError"
                                defaultMessage="Please upload a 1600px by 400px JPEG."
                            />
                        </div>
                    )}
                    <div className={styles.headerContent}>
                        {uploadIcon}
                        <ProfileHeaderBreadcrumbs tradeFirm={(tradeFirm: ?Object)} />
                        <ProfileHeaderData
                            geography={(geography || null: ?Object)}
                            tradeFirm={(tradeFirm: ?Object)}
                            isEditMode={isEditMode}
                            currentPage={currentPage}
                            onChangeTab={this.props.onChangePage}
                        />
                    </div>
                    <div className={styles.photoOverlay}>
                        {this.state.isSaving && <Spinner size="small" />}
                    </div>
                </div>
            </div>
        );
    }
}

export const HeaderRoot = createFragmentContainer(HeaderRootComponent, {
    viewer: graphql`
        fragment HeaderRoot_viewer on Viewer {
            geography @include(if: $isEditMode) {
                ...ProfileHeaderData_geography
            }
            tradeFirms(urlLabel: $firmUrlLabel) {
                ...ProfileHeaderBreadcrumbs_tradeFirm
                ...ProfileHeaderData_tradeFirm
                ...ProgressTour_tradeFirm @include(if: $isEditMode)
                serviceId
                profileStatus
                publicProfile {
                    splashPhoto
                    profilePhotoId
                    designProjectsCount
                }
            }
        }
    `,
});
